import { add, addDays, addMinutes, addMonths, endOfTomorrow, format, lastDayOfMonth, parseISO, set, startOfTomorrow } from 'date-fns';

export class DateUtil {
  /**
   * Minutes difference between local and UTC
   */
  static timeZoneDifference(dateReference?: Date): number {
    const date = dateReference ?? new Date();
    // Cogemos la diferencia respecto al timezone
    const timeZoneOffsetInMinutes = date.getTimezoneOffset();
    // Como nos devuelve la diferencia negativa, la covertimos para tener la diferencia real en positivo
    return -timeZoneOffsetInMinutes;
  }
  static yesterdayUTC(hours: number = 0, min: number = 0, sec: number = 0, ms: number = 0): Date {
    const date = DateUtil.todayUTC(hours, min, sec, ms);
    return addDays(date, -1);
  }
  static todayUTC(hours: number = 0, min: number = 0, sec: number = 0, ms: number = 0): Date {
    const today = new Date();
    today.setUTCFullYear(today.getFullYear(), today.getMonth(), today.getDate());
    today.setUTCHours(hours, min, sec, ms);
    return today;
  }

  /**
   * Sets a time string to a Date
   * @throws Error if the length of time string is not 4
   */
  static setTimeToDate(date?: Date | string, time?: string): Date | undefined {
    if (date == null || time == null) {
      return undefined;
    }
    const dateTime = new Date(date);
    if (time.length !== 4) {
      throw new Error('TIME IS INVALID');
    }
    const hour = time.substring(0, 2);
    const minutes = time.substring(2);
    dateTime.setHours(parseInt(hour), parseInt(minutes));
    return dateTime;
  }

  static tomorrow(hours: number = 0, min: number = 0, sec: number = 0, ms: number = 0): Date {
    return addDays(DateUtil.todayUTC(hours, min, sec, ms), 1);
  }

  static nextMonth(hours: number = 23, min: number = 59, sec: number = 59, ms: number = 0): Date {
    return addDays(DateUtil.todayUTC(hours, min, sec, ms), 30);
  }

  static buildUTCDate(str: string): Date {
    if (!str.endsWith('Z')) {
      return new Date(str.concat('Z'));
    }
    return new Date(str);
  }

  static oneDayAfter(oriDate: Date, hours: number = 0, min: number = 0, sec: number = 0, ms: number = 0): Date {
    const date = new Date(oriDate);
    date.setUTCFullYear(date.getFullYear(), date.getMonth(), date.getDate());
    date.setUTCHours(hours, min, sec, ms);
    return addDays(date, 1);
  }

  static dateBetweenDates(date: Date, dateStart: Date, dateEnd: Date): boolean {
    // Pasamos fechas a milisegundos para compararlas y la de end la ponemos en las 23:59:59 para que sea al finalizar el dia
    return date.getTime() > dateStart.getTime() && date.getTime() < dateEnd.setHours(23, 59, 59);
  }

  static isFuture30Days(date: Date): boolean {
    return date.getTime() >= startOfTomorrow().getTime() && date.getTime() <= add(endOfTomorrow(), { days: 30 }).getTime();
  }

  static startOfMonth(oriDate: Date, hours: number = 0, min: number = 0, sec: number = 0, ms: number = 0): Date {
    const date = new Date(oriDate);
    date.setUTCFullYear(date.getFullYear(), date.getMonth(), 1);
    date.setUTCHours(hours, min, sec, ms);
    return date;
  }

  static oneDayBefore(oriDate: Date, hours: number = 0, min: number = 0, sec: number = 0, ms: number = 0): Date {
    const date = new Date(oriDate);
    date.setUTCFullYear(date.getFullYear(), date.getMonth(), date.getDate());
    date.setUTCHours(hours, min, sec, ms);
    return addDays(date, -1);
  }

  static getCurrentTime(): string {
    return format(new Date(), 'HHmm');
  }

  static previousNaturalMonth(date?: Date): { start: Date; end: Date } {
    const origin = date ?? new Date();
    const lastDayOfLastMonth = addMinutes(lastDayOfMonth(addMonths(origin, -1)), DateUtil.timeZoneDifference());
    const lastDayOfTwoMonths = lastDayOfMonth(addMonths(origin, -2));
    const firstDayOfLastMonth = addMinutes(addDays(lastDayOfTwoMonths, 1), DateUtil.timeZoneDifference());
    return {
      start: firstDayOfLastMonth,
      end: lastDayOfLastMonth,
    };
  }

  static previousNaturalMonths(amountOfMonths = 6): { start: Date; end: Date } {
    const origin = new Date();
    const end = set(lastDayOfMonth(addMonths(origin, -1)), {
      hours: 23,
      minutes: 59,
      seconds: 59,
    });
    const start = set(addMonths(end, 1 - amountOfMonths), {
      date: 1,
      hours: 0,
      minutes: 0,
      seconds: 0,
    });
    return {
      start,
      end,
    };
  }

  static toBackString(date?: Date | undefined) {
    if (!date) {
      return undefined;
    }
    return `${format(date, 'yyyy-MM-dd')}T${format(date, 'HH:mm:SS')}Z`;
  }

  static fromBackString(input?: string | undefined): Date | undefined {
    const dateString = DateUtil.dateStringWoutZ(input);
    if (!dateString) {
      return undefined;
    }
    return parseISO(dateString);
  }
  static dateStringWoutZ(fechaHoraAveria?: string): string | undefined {
    if (!fechaHoraAveria) {
      return undefined;
    }
    return fechaHoraAveria.endsWith('Z') ? fechaHoraAveria.slice(0, fechaHoraAveria.length - 1) : fechaHoraAveria;
  }
}
